import {GitPullRequestClosedIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'

import {ForYouItem} from '../shared/ForYouItem'
import {ForYouLeadingIcon} from '../shared/ForYouLeadingIcon'
import {useLayoutRouteData} from '../../../../page-data/loaders/use-layout-route-data'
import {PullRequestState} from '../../../../types'
import type {StackData} from '../../../../page-data/payloads/header'

export type StackPullRequest = StackData['pulls'][number]

/**
 * Finds the closest closed downstack pull request, if any. `stack.pulls` is ordered head to
 * base, so downstack entries are the ones after the current pull request in the array.
 */
export function findClosedDownstackPullRequest(
  stack: StackData | null | undefined,
  pullRequestNumber: number,
): StackPullRequest | undefined {
  if (!stack) return undefined

  const currentIndex = stack.pulls.findIndex(pull => pull.number === pullRequestNumber)
  if (currentIndex === -1) return undefined

  return stack.pulls.slice(currentIndex + 1).find(pull => pull.state === PullRequestState.Closed)
}

export function shouldShowStackClosed(stack: StackData | null | undefined, pullRequestNumber: number): boolean {
  return findClosedDownstackPullRequest(stack, pullRequestNumber) !== undefined
}

export function StackClosed({variant}: {variant?: 'default' | 'embedded'}) {
  const {pullRequest, stack} = useLayoutRouteData()
  const closedPullRequest = findClosedDownstackPullRequest(stack, pullRequest.number)

  if (!closedPullRequest) return null

  return (
    <ForYouItem
      leadingVisual={<ForYouLeadingIcon icon={GitPullRequestClosedIcon} variant="danger" emphasized />}
      emphasizedLeadingVisual
      title="A previous stacked PR was closed"
      itemName="stack-closed"
      variant={variant}
      description={
        <>
          Reopen or remove{' '}
          <Link inline className="Link--inTextBlock" href={closedPullRequest.url}>
            #{closedPullRequest.number}
          </Link>{' '}
          before merging this pull request.
        </>
      }
    />
  )
}
